

import React from 'react';
import { FEATURES, FeatureKey } from '../services/features';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRoomUnreadCounts, countRoomsUnread } from '../services/roomUnreadCounts';
import { useUnreadEmailCount } from '../services/emailUnreadBadge';
import { useAppSelector, useAppActions } from '../context/AppContext';
import { useNotifications } from './NotificationHandler';
import { UserRole } from '../types';
import { usePaymentsAccess } from '../services/etransferNotices';
import { useCustomerLookup, CUSTOMER_LOOKUP_GRANTABLE } from '../services/customerLookup';
import { useIdReviewCount } from '../services/customerIdentities';
import { LayoutDashboard, Package, Truck, ClipboardList, ArrowLeftRight, Users, PackageSearch, FileText, Settings, X, Radar, LogOut, PieChart, History, Activity, MessageSquare, Mail, BarChart3, Boxes, UserCircle, MapPin, MapPinned, Factory, HardHat, Bot, CreditCard, BadgeDollarSign, Gauge, Smartphone, SquareKanban, ShieldCheck, MailOpen } from 'lucide-react';
import { roleLabel } from '../services/roleLabel';

// Customers (0135): the owner, and the roles the owner may switch on for search-only lookup.
const CUSTOMERS_ROLES = [UserRole.OWNER, ...CUSTOMER_LOOKUP_GRANTABLE];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const currentUser = useAppSelector(s => s.currentUser);
  // Payments (0116): the super admin's, plus the users the super admin grants in the Admin Panel.
  const paymentsAccess = usePaymentsAccess(currentUser);
  // Customers: the owner's full list, or search-only for whoever the owner switched on (0135).
  const customerLookup = useCustomerLookup(currentUser);
  const rooms = useAppSelector(s => s.rooms);
  const { logout } = useAppActions();
  const { unreadCount, unreadByChannel, unreadTexts } = useNotifications();
  // The Chat badge counts CONVERSATIONS with unread, not messages (per Umer,
  // 2026-08-28): unreadCount is already the order+direct conversation count,
  // and rooms contribute one each. Three unread messages in a single chat
  // badge as 1; per-chat rows keep their message counts. Caps at 9+.
  const roomCounts = useRoomUnreadCounts(rooms, currentUser?.id);
  const chatUnread = unreadCount + countRoomsUnread(roomCounts);
  // Email badge (Umer, 2026-09-16): unread inbox messages, mailbox-wide, the
  // same count the Email page shows. Only counted for whoever can open the
  // Email item — the roles listed on it below; hooks must run before the
  // early return, so the check lives here rather than on the menu entry.
  const unreadEmails = useUnreadEmailCount(
    FEATURES.emailInbox &&
      !!currentUser &&
      [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH].includes(currentUser.role)
  );
  // ID reviews badge (0128): website ID checks waiting for a person, for the queue entry (the dispatch tier
  // and the super admin).
  const idReviews = useIdReviewCount(
    FEATURES.idVerification &&
      !!currentUser &&
      [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER].includes(currentUser.role)
  );
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active tab from current URL (strip leading slash).
  // Empty path (/) falls back to empty — nothing is highlighted on root redirect.
  const activeTab = location.pathname.replace(/^\//, '').split('/')[0];

  if (!currentUser) return null;

  const menuItems: Array<{ id: string; label: string; icon: React.ComponentType<{ size?: number; className?: string }>; roles: UserRole[]; feature?: FeatureKey }> = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'store_hub', label: 'Store Hub', icon: Truck, roles: [UserRole.STORE] },
    { id: 'driver_app', label: 'Driver App', icon: Truck, roles: [UserRole.DRIVER] },
    // Warehouse hierarchy (2026-08-28, per the owner):
    //   Super Admin / Admin → WM hub + Packaging House hub
    //   Warehouse Manager  → both
    //   Packaging House    → one merged hub (production + confirm chips).
    //     Stored role id stays 'worker'; legacy 'packager' users see the same
    //     merged hub until their role is flipped. No separate Packager tab.
    //   Warehouse-pipeline roles get NO Chat — deliberate (removed 2026-08-28).
    { id: 'warehouse_manager', label: 'Warehouse', icon: Factory, roles: [UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER] },
    { id: 'worker', label: 'Packaging House', icon: HardHat, roles: [UserRole.WORKER, UserRole.PACKAGER, UserRole.WAREHOUSE_MANAGER, UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER] },
    { id: 'worker_report', label: 'My Activity', icon: FileText, roles: [UserRole.WORKER, UserRole.PACKAGER] },
    { id: 'messages', label: 'Chat', icon: MessageSquare, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STORE, UserRole.DRIVER] },
    { id: 'agent_console', label: 'AI Agent', icon: Bot, feature: 'agentConsole', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'inbox', label: 'Email', icon: Mail, feature: 'emailInbox', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    // Email Templates (2026-10-30): the customer emails' wording, edited by the admin tier (it writes settings, RLS).
    { id: 'email_templates', label: 'Email Templates', icon: MailOpen, feature: 'orderBoard', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    { id: 'dispatch_oversight', label: 'Dispatch Oversight', icon: Gauge, feature: 'dispatchOversight', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    // Driver Control (UPE-119): GPS rule, out-of-shift, who is working today. Admin tier only.
    { id: 'driver_control', label: 'Driver Control', icon: Radar, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    // Team task board (0091, 2026-09-24): who is working on what. Same roles as services/tasks TASK_BOARD_ROLES.
    { id: 'tasks', label: 'Task Management', icon: SquareKanban, feature: 'taskBoard', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER, UserRole.STOCK_MANAGER, UserRole.WAREHOUSE_MANAGER, UserRole.WORKER, UserRole.PACKAGER, UserRole.TEAM_MEMBER] },
    { id: 'text_inbox', label: 'Text Inbox', icon: Smartphone, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'admin_reports', label: 'Financial Reports', icon: PieChart, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    { id: 'products', label: 'Storage House', icon: Package, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STOCK_MANAGER, UserRole.MANAGER] },
    { id: 'warehouse_history', label: 'Warehouse History', icon: History, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER] },
    { id: 'full_history', label: 'Full Activity Log', icon: Activity, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    // Store Stock removed from sidebar — no stores in production yet
    // { id: 'store_stock_search', label: 'Store Stock', icon: PackageSearch, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'orders', label: 'Orders', icon: ClipboardList, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'payment_approval', label: 'Order Board', icon: CreditCard, feature: 'orderBoard', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    // E-transfer inbox (0116): every Interac notice, linked to its card. Shown only with Payments access
    // (super admin, or granted in the Admin Panel) — see the filter below.
    { id: 'payments', label: 'Payments', icon: BadgeDollarSign, feature: 'orderBoard', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'delivery_map', label: 'Delivery Map', icon: MapPin, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'shipping_labels', label: 'Shipping Labels', icon: MapPinned, feature: 'shipping', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'store_reports', label: 'Store Reports', icon: FileText, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'driver_reports', label: 'Driver Reports', icon: FileText, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH] },
    { id: 'warehouse_reports', label: 'Warehouse Reports', icon: FileText, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.WAREHOUSE_MANAGER, UserRole.MANAGER] },
    { id: 'store_handoff', label: 'Store Handoffs', icon: ArrowLeftRight, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'inventory', label: 'Inventory Ops', icon: ArrowLeftRight, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'inventory_tracker', label: 'Inventory Tracker', icon: BarChart3, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'stock_overview', label: 'Stock Overview', icon: Boxes, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.STOCK_MANAGER, UserRole.MANAGER] },
    { id: 'users', label: 'Admin Panel', icon: Users, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN] },
    { id: 'customers', label: 'Customers', icon: UserCircle, roles: CUSTOMERS_ROLES },
    // ID verification (0126, 2026-10-10): one customer per email + phone pair, with the ID check. Super admin only.
    { id: 'id_verification', label: 'ID Verification', icon: ShieldCheck, feature: 'idVerification', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN] },
    // The website ID check's review queue (0128, 2026-10-13): only pairs waiting for a person. The dispatch
    // tier works it; super admins see it too, next to the whole section above (2026-10-14).
    { id: 'id_reviews', label: 'ID reviews', icon: ShieldCheck, feature: 'idVerification', roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.MANAGER] },
    { id: 'settings', label: 'Settings', icon: Settings, roles: [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH, UserRole.STORE, UserRole.DRIVER, UserRole.WAREHOUSE_MANAGER, UserRole.WORKER, UserRole.PACKAGER, UserRole.STOCK_MANAGER, UserRole.MANAGER, UserRole.TEAM_MEMBER] },
  ];

  // Role first, then the main-parity gate (services/features.ts): an item
  // tagged with a feature disappears entirely when that feature is off.
  const filteredMenu = menuItems.filter(item =>
    item.roles.includes(currentUser.role) && (!item.feature || FEATURES[item.feature as FeatureKey])
    && (item.id !== 'payments' || paymentsAccess)
    && (item.id !== 'customers' || customerLookup === true)
  );

  const handleNavigation = (id: string) => {
      navigate(`/${id}`);
      // Close sidebar on mobile when item clicked
      if (window.innerWidth < 768) {
          onClose();
      }
  };

  return (
    <>
        {/* Mobile Overlay */}
        {isOpen && (
            <div 
                className="fixed inset-0 bg-black/50 z-40 md:hidden animate-in fade-in"
                onClick={onClose}
            />
        )}

        {/* Sidebar */}
        <div className={`
            fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transition-transform duration-300 ease-in-out flex flex-col
            md:translate-x-0 md:sticky md:top-0 md:h-screen
            ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
            <div className="p-6 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center font-bold text-lg shadow-lg shadow-brand-900/50">
                        W
                    </div>
                    <span className="text-xl font-bold tracking-tight">WolfDrivers</span>
                </div>
                <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
                    <X size={24} />
                </button>
            </div>

            <div className="px-4 py-2 shrink-0">
                <div className="bg-slate-800/50 rounded-xl p-3 flex items-center gap-3 border border-slate-700">
                    <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-700 rounded-full flex items-center justify-center text-sm font-bold shadow-inner">
                        {currentUser.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold capitalize truncate">{currentUser.name}</p>
                        <p className="text-xs text-slate-400 capitalize truncate">{roleLabel(currentUser.role)}</p>
                    </div>
                </div>
            </div>

            <nav className="flex-1 mt-6 px-4 space-y-1.5 overflow-y-auto min-h-0 pb-2">
                {filteredMenu.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                        <button
                            key={item.id}
                            onClick={() => { handleNavigation(item.id); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                                isActive
                                ? 'bg-brand-600 text-white shadow-lg shadow-brand-900/20'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`}
                        >
                            <Icon size={20} className={isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'} />
                            {item.label}
                            {item.id === 'inbox' && unreadEmails > 0 && (
                                <span className={`ml-auto text-[10px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 ${
                                    isActive ? 'bg-white text-brand-700' : 'bg-brand-500 text-white' // the row itself is brand-600 when active
                                }`}>
                                    {unreadEmails > 9 ? '9+' : unreadEmails}
                                </span>
                            )}
                            {item.id === 'id_reviews' && idReviews > 0 && (
                                <span
                                    data-testid="sidebar-id-reviews-count"
                                    className="ml-auto bg-amber-500 text-white text-[10px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
                                >
                                    {idReviews > 9 ? '9+' : idReviews}
                                </span>
                            )}
                            {item.id === 'text_inbox' && unreadTexts > 0 && (
                                <span className="ml-auto bg-green-500 text-white text-[10px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                                    {unreadTexts > 9 ? '9+' : unreadTexts}
                                </span>
                            )}
                            {item.id === 'messages' && chatUnread > 0 && (
                                <span className="ml-auto flex items-center gap-1">
                                    {unreadByChannel.customer > 0 && (
                                        <span
                                            title={`${unreadByChannel.customer} customer SMS reply${unreadByChannel.customer === 1 ? '' : 'ies'}`}
                                            className="bg-green-500 text-white text-[9px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
                                        >
                                            SMS
                                        </span>
                                    )}
                                    <span className="bg-red-500 text-white text-[10px] font-black min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                                        {chatUnread > 9 ? '9+' : chatUnread}
                                    </span>
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>

            <div className="shrink-0 p-4 border-t border-slate-800">
                <button
                    onClick={logout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-bold text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                >
                    <LogOut size={18} />
                    Sign Out
                </button>
            </div>
        </div>
    </>
  );
};